import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Cuisines from '../areas/landingPages/pages/Cuisines';
import DynamicLandingPage from '../areas/landingPages/pages/DynamicLandingPage';
import withI18n from '../components/withI18n';
import { requestConfig } from '../actions/configActions';
import { requestRecentAddresses } from '../actions/locationActions';
import {
  requestCuisineForCountry,
  requestCuisineForCity,
  getRestaurantsByAddresses,
} from '../actions/cuisineActions';
import { brandSuggestedMenuSearch } from '../actions/brandActions';
import { requestLandingPage } from '../areas/landingPages/actions';
import { statusForState } from '../areas/landingPages/utils';
import { isCuisineRestaurantsOn } from '../utils/pages/cuisines';
import sentry from '../utils/sentry';
import CustomErrorPage from './_error';

const { captureException } = sentry();

class LandingPage extends Component {
  static async getPageData(context, dispatch, getState) {
    const { req, res, query } = context;
    // since landing-page.js is the entry point for Storyblok-driven landing
    // pages *and* both existing Cuisine Landing Pages and Brand Landing Pages,
    // we need to first query whether a story exists for the requested path; if
    // it does, attempt to render the page that way, otherwise attempt to fall
    // back to an older generation Landing Page, and dispatch the pre-requisite
    // actions.

    const actions = [
      dispatch(requestConfig()),
      dispatch(requestRecentAddresses()),
      dispatch(requestLandingPage({ path: query.path, host: req.country.host })),
    ];

    const landingPageType = query.cuisine ? 'cuisines' : 'brand';
    const { brand } = query;
    let statusForStateOptions = {};

    // eslint-disable-next-line default-case
    switch (landingPageType) {
      case 'cuisines':
        {
          const cuisineAction = query.city ? requestCuisineForCity : requestCuisineForCountry;
          actions.push(dispatch(cuisineAction(query)));
          await Promise.all(actions);
        }
        break;
      case 'brand':
        statusForStateOptions = { ...statusForStateOptions, brands: { current: brand } };
        await Promise.all(actions).then(() => dispatch(brandSuggestedMenuSearch({ brand })));
        break;
    }

    const { config, location, landingPage } = getState();
    statusForStateOptions = {
      ...statusForStateOptions,
      config,
      landingPage,
    };
    const { status: landingPageStatus, landingPageError } = statusForState(statusForStateOptions);
    const cuisineRestaurantsOn = isCuisineRestaurantsOn(config);

    if (res && landingPageStatus) {
      res.statusCode = landingPageStatus;

      switch (landingPageStatus) {
        case 404: {
          // based on the response here we need to then take action: if it’s a
          // 404, determine whether it’s a cuisine or brand landing page, and
          // then attempt to dispatch the appropriate action(s) instead, and
          // handle the outcome of _that_ action.

          switch (landingPageType) {
            case 'cuisines':
              {
                if (cuisineRestaurantsOn) {
                  const { locations } = location.recentAddresses;
                  await dispatch(getRestaurantsByAddresses(locations));
                }

                // Now check the cuisines page status so that we can capture errors
                // with cuisine requests too.
                const { cuisine } = getState();
                const { status: cuisinePageStatus, error: cuisinePageError } = statusForState({
                  config,
                  cuisine,
                });

                if (res && cuisinePageStatus) {
                  res.statusCode = cuisinePageStatus;
                  res.page = 'cuisines';

                  if (cuisinePageError) {
                    captureException(cuisinePageError);
                  }
                }
              }
              break;
            case 'brand':
              {
                // Now check the brand page status so that we can capture errors
                // with brand requests too.
                const state = getState();
                const { status: brandPageStatus, error: brandPageError } = statusForState({
                  config,
                  brand: state.brand,
                });

                if (res && brandPageStatus) {
                  res.statusCode = brandPageStatus;
                  res.page = 'brand';

                  if (brandPageError) {
                    captureException(brandPageError);
                  }
                }
              }
              break;
            default:
              captureException(landingPageError);
              break;
          }
          break;
        }
        default:
        // pass through if there were no errors
      }
    }
  }

  render() {
    const { statusCode, pageType } = this.props;

    if (statusCode === 200) {
      // eslint-disable-next-line default-case
      switch (pageType) {
        case 'cuisines':
          return <Cuisines />;
        default:
          // i.e. 'landing-page'
          return <DynamicLandingPage />;
      }
    } else {
      return <CustomErrorPage statusCode={statusCode} />;
    }
  }
}

LandingPage.propTypes = {
  statusCode: PropTypes.number.isRequired,
  pageType: PropTypes.oneOf(['landing-page', 'cuisines']),
};

LandingPage.defaultProps = {
  pageType: undefined,
};

function mapStateToProps(state) {
  const { status, page } = statusForState(state);

  return {
    statusCode: status,
    pageType: page,
  };
}

export default connect(mapStateToProps)(withI18n(LandingPage));
